import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { getNewsById, getRelatedNews } from "@/lib/media";

export default function NewsDetailPage() {
  const { id } = useParams();
  const item = id ? getNewsById(id) : undefined;
  const [toast, setToast] = useState<string | null>(null);

  const related = useMemo(() => (item ? getRelatedNews(item, 3) : []), [item]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  usePageMeta({
    title: item ? `${item.title} - 91蜜桃资讯` : "资讯详情 - 91蜜桃",
    description: item ? item.summary : "91蜜桃影视资讯详情页。",
    keywords: item ? `91蜜桃资讯,${item.title},${item.category}` : "91蜜桃资讯",
  });

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-newspaper-line"
          title="抱歉，没有找到这篇资讯"
          desc="内容可能已被移除或链接有误，去看看其他资讯吧。"
          backHome
        />
      </div>
    );
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("页面链接已复制");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-7">
          <Breadcrumb items={[{ label: "资讯", to: "/news" }, { label: item.category }]} />
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-12">
          <article className="min-w-0">
            <span className="inline-flex rounded-md bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
              {item.category}
            </span>
            <h1 className="mt-3 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-3xl">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {item.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {item.date}
              </span>
              <button
                type="button"
                onClick={handleShare}
                className="ml-auto flex items-center gap-1 whitespace-nowrap text-primary-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
            </div>

            <div className="mt-6 space-y-5">
              {item.content.map((paragraph, index) => (
                <div key={index}>
                  <p className="text-base leading-8 text-foreground-800">{paragraph}</p>
                  {index === 1 && (
                    <figure className="mt-5">
                      <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 91蜜桃影视资讯`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <figcaption className="mt-2 text-center text-xs text-foreground-400">
                        配图 / 91蜜桃资讯
                      </figcaption>
                    </figure>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="text-sm text-foreground-500">
                本文由 91蜜桃影视资讯频道整理发布，仅代表作者观点。内容仅供学习交流，不构成任何观看或投资建议。
              </p>
            </div>
          </article>

          <aside className="min-w-0">
            <h2 className="font-heading text-lg font-bold text-foreground-950">相关资讯</h2>
            <div className="mt-4 space-y-3">
              {related.map((news) => (
                <Link
                  key={news.id}
                  to={`/news/${news.id}`}
                  className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-sm transition-all duration-300 hover:shadow-md"
                >
                  <div className="h-32 w-full overflow-hidden bg-background-200">
                    <img
                      src={news.cover}
                      alt={news.title}
                      title={`${news.title} - 91蜜桃影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3.5">
                    <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                      {news.title}
                    </h3>
                    <p className="mt-1.5 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-calendar-line" />
                      {news.date}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <Link
              to="/news"
              className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              返回资讯列表
              <i className="ri-arrow-right-line" />
            </Link>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-secondary-950 px-5 py-2.5 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}